import React from 'react';
import {
  ExtendedIngredientDTO,
  IngredientDocStockDTO,
  MergeIngredientDTO,
} from 'business/models/Models';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { setSelectedIngredientList } from 'redux/reducers/experimentReducer';
import {
  setIngTableDataIndicator,
  setIngredientSelectedRows,
} from 'redux/reducers/ingredientTableReducer';
import { showAlertSnackBar } from 'utils/Global';
import { mergeIngredients } from 'business/services/IngredientService';
import { isEqual, cloneDeep, uniq } from 'lodash';
import { AddMergeIngredientProps } from './MergeIngredientConstants';
import { setMergingStocks } from '../mergeStocks/MergeStockUtils';
import { ButtonsListContainer } from 'components/Button';

interface mergeIngredientsBtnProps {
  setMergingStocks: (...arg: any) => void;
  setStockIndex: (value: number) => void;
  ingredients: ExtendedIngredientDTO[];
  mergeIngredient: ExtendedIngredientDTO;
  setIngredients: (value: ExtendedIngredientDTO[]) => void;
  setShowModal: (value: boolean) => void;
  setMergeIngredient: (value: ExtendedIngredientDTO) => void;
  setMatchedIngStocks: (value: { [key: string]: IngredientDocStockDTO[] }) => void;
  matchedIngStocks: { [key: string]: IngredientDocStockDTO[] };
  mergedIngredient: ExtendedIngredientDTO;
  stockIndex: number;
  setStocks: (value: IngredientDocStockDTO[]) => void;
  setMatchedStocks: (value: IngredientDocStockDTO[]) => void;
  setStocksShowModal: (value: boolean) => void;
  dispatch: any;
  ingTableDataIndicator: number;
  excludedStockIds: number[];
  setIsMerging: (value: boolean) => void;
}

const contentContainerClass = 'setup-btn-outline-yellow-wide btn-outline-yellow-wide';

export const MergeIngredientsButtons: React.FC<mergeIngredientsBtnProps> = ({
  stockIndex,
  setStockIndex,
  setStocks,
  setMatchedStocks,
  setStocksShowModal,
  ingredients,
  mergeIngredient,
  setIngredients,
  setShowModal,
  setMergeIngredient,
  setMatchedIngStocks,
  mergedIngredient,
  dispatch,
  ingTableDataIndicator,
  excludedStockIds,
}) => {
  const [isMerging, setIsMerging] = React.useState(false);

  const cancelClick = React.useCallback(() => {
    setIngredients([]);
    setShowModal(false);
    setStockIndex(0);
  }, [setIngredients, setShowModal, setStockIndex]);

  const validateBuffers = (mergeIngredient: ExtendedIngredientDTO) => {
    if (!ingredients || !ingredients[0] || !ingredients[1]) {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, 'Invalid ingredients');
      return false;
    }
    const bothBuffers =
      ingredients[0].types.some((t) => t.isBuffer) &&
      ingredients[1].types.some((t) => t.isBuffer);

    const resultNotBuffer = !mergeIngredient.types.some((t) => t.isBuffer);

    if (bothBuffers && resultNotBuffer) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'One of the ingredients is a buffer. The resulting ingredient must also be a buffer. Check your work and try again.',
      );
      return false;
    }
    return true;
  };

  const handleMergeSuccess = async (
    matchedIngStocks: { [key: string]: IngredientDocStockDTO[] },
    tempMergeIngredient: ExtendedIngredientDTO,
  ) => {
    if (!ingredients || !ingredients[0] || !ingredients[1]) {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, 'Invalid ingredients');
      return;
    }
    const combinedStocks = uniq([
      ...ingredients[0].ingredientStock,
      ...ingredients[1].ingredientStock,
    ]);

    tempMergeIngredient.ingredientStock = combinedStocks;
    setMergeIngredient(tempMergeIngredient);
    setMatchedIngStocks(matchedIngStocks);

    await setMergingStocks({
      matchedIngStocks,
      ingredients,
      mergedIngredient,
      stockIndex,
      setStocks,
      setMatchedStocks,
      setStockIndex,
      setStocksShowModal,
      setShowModal,
      dispatch,
      ingTableDataIndicator,
      excludedStockIds,
    });
  };

  const mergeClick = React.useCallback(async () => {
    if (!validateMergeIngredient(mergeIngredient) || !validateBuffers(mergeIngredient)) {
      return;
    }

    setIsMerging(true);
    try {
      const tempMergeIngredient: ExtendedIngredientDTO = mergeIngredient;
      // array element validation has been performed in validateBuffers method
      const excludedId =
        ingredients[0].id === tempMergeIngredient.id
          ? ingredients[1].id
          : ingredients[0].id;

      const resp = await mergeIngredients(
        new MergeIngredientDTO({
          excludedIngredientId: excludedId,
          ingredient: tempMergeIngredient,
          isStockAlreadyMatched: false,
          excludedStockIds,
        }),
      );

      if (!resp) {
        // reset everything if nothing is returned
        dispatch(setIngredientSelectedRows([]));
        dispatch(setIngTableDataIndicator(ingTableDataIndicator + 1));
        setIngredients([]);
        dispatch(setSelectedIngredientList([]));
        setShowModal(false);
      } else {
        await handleMergeSuccess(resp, tempMergeIngredient);
      }
    } finally {
      setIsMerging(false);
    }
  }, [
    mergeIngredient,
    ingredients,
    excludedStockIds,
    mergedIngredient,
    stockIndex,
    setStocks,
    setMatchedStocks,
    setStockIndex,
    setStocksShowModal,
    setShowModal,
    dispatch,
    ingTableDataIndicator,
  ]);

  const isDisabled = isMerging,
    showBorder = true;
  return (
    <ButtonsListContainer
      containerStyleClass="commands-container"
      buttonsList={[
        {
          title: 'Merge',
          onClick: mergeClick,
          contentContainerClass,
          isDisabled,
          showBorder,
        },
        {
          title: 'Cancel',
          onClick: cancelClick,
          contentContainerClass,
          isDisabled,
          showBorder,
        },
      ]}
    />
  );
};

const validateMergeIngredient = (mergeIngredient: ExtendedIngredientDTO) => {
  let isValid = false;
  if (!mergeIngredient.id) {
    showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, 'Type an ID.');
  } else if (!mergeIngredient.longName) {
    showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, 'Type an long name.');
  } else if (!mergeIngredient.shortName) {
    showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, 'Type an short name.');
  } else if (
    mergeIngredient.types?.find((type) => type.isBuffer === true) &&
    !mergeIngredient.bufferCalculationTypeId
  ) {
    showAlertSnackBar(
      EAlertType.ERROR,
      ESnackBarPosition.TOP,
      'Enter buffer calculation data.',
    );
  } else {
    isValid = true;
  }
  return isValid;
};

export const getCombinedMergeTypes = (ingredients: ExtendedIngredientDTO[]) => {
  return ingredients[0].types.concat(
    ingredients[1].types.filter(
      (type) => !ingredients[0].types.find((t) => t.name === type.name),
    ),
  );
};

export const getCombinedMergeIngIngTypes = (
  mergeIng: ExtendedIngredientDTO,
  ingredients: ExtendedIngredientDTO[],
) => {
  if (!mergeIng.ingredientIngredientType) {
    mergeIng.ingredientIngredientType = [];
  }
  mergeIng.types.map((type) => {
    if (!mergeIng.ingredientIngredientType?.find((t) => t.ingredientTypeId === type.id)) {
      let tempIngIngType = ingredients[0].ingredientIngredientType?.find(
        (t) => t.ingredientTypeId === type.id,
      );
      if (!tempIngIngType) {
        tempIngIngType = ingredients[1].ingredientIngredientType?.find(
          (t) => t.ingredientTypeId === type.id,
        );
      }
      mergeIng.ingredientIngredientType.push(tempIngIngType);
    }
  });
  return mergeIng.ingredientIngredientType;
};

export const addMergeIngredient = (props: AddMergeIngredientProps) => {
  const { ing, property, mergeIngredient, setMergeIngredient, setTempIngredient } = props;
  const ingredient = mergeIngredient;
  const tempIng = new ExtendedIngredientDTO();
  if (property === 'bufferCalculationTypeId') {
    if (ing[property]) {
      ingredient[property] = ing[property];
      ingredient.pKa = ing.pKa;
      ingredient.bufferDataPoint = ing.bufferDataPoint?.map((bufferDataPoint) => {
        bufferDataPoint.ingredientId = ingredient.id;
        return bufferDataPoint;
      });
    }
  } else if (Array.isArray(ingredient[property])) {
    ing[property]?.map((item) => {
      if (!ingredient[property].some((innerItem) => isEqual(innerItem, item))) {
        ingredient[property].push(item);
      }
    });
  } else {
    ingredient[property] = ing[property];
  }
  setMergeIngredient && setMergeIngredient(cloneDeep(ingredient));
  setTempIngredient && setTempIngredient(cloneDeep(tempIng));
};
